import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";

export const UserPrivacyPolicy = () => {
  return (
    <div className="p-4 space-y-3 text-sm text-[#404040] leading-6">
      <p>
        <strong>Effective Date:</strong> 01 Aug 2026
        <br />
        <strong>Last Updated:</strong> 01 Aug 2026
      </p>
      <p>
        This Privacy Policy explains how{" "}
        <strong>Fluxart Solutions Private Limited</strong>, operating the{" "}
        <strong>Mumin&#39;s Mart</strong> website, mobile application, and
        related services (collectively, the &quot;Platform&quot;), collects,
        uses, discloses, stores, and protects personal data of users in India.
      </p>
      <p>
        By using the Platform, you agree to this Privacy Policy and our Terms
        and Conditions.
      </p>
      <h2 className="font-semibold">1. Who We Are</h2>
      <ul>
        <li>Entity: Fluxart Solutions Private Limited</li>
        <li>
          Registered Office: A78 Hasanji Nagar, Near Emerald Heights School,
          Indore - 453331
        </li>
        <li>Contact Email: support@muminsmart.com</li>
        <li>Grievance Officer Email: grievance@muminsmart.com</li>
      </ul>
      <p>
        For the purposes of applicable Indian law, including the Digital
        Personal Data Protection Act, 2023 (&quot;DPDP Act&quot;) and the
        Information Technology Act, 2000, Fluxart Solutions Private Limited acts
        as the data fiduciary for user personal data processed through the
        Platform.
      </p>
      <h2 className="font-semibold">2. Personal Data We Collect</h2>
      <p>We may collect the following categories of personal data:</p>
      <ul>
        <li>
          Identity data: name, date of birth (if required), profile details.
        </li>
        <li>
          Contact data: mobile number, email address, shipping and billing
          address.
        </li>
        <li>Account data: username, password hash, account preferences.</li>
        <li>
          Transaction data: order history, payment status, invoices, refunds.
        </li>
        <li>
          Communication data: customer support messages, reviews, feedback.
        </li>
        <li>
          Device and usage data: IP address, browser/app type, device
          identifiers, logs, pages/screens viewed, session timestamps.
        </li>
        <li>
          Location data: approximate or precise location (only when enabled by
          you).
        </li>
        <li>
          KYC or verification data (if required): government-issued identifiers
          or documents as required by law.
        </li>
        <li>
          Community verification data: ITS details may be collected only to
          confirm that a user belongs to the community.
        </li>
      </ul>
      <p>
        ITS details are used strictly for community verification and are not
        shared with third parties, except where disclosure is required by
        applicable law.
      </p>
      <p>
        We do not intentionally collect more data than necessary for the
        purposes listed in this policy.
      </p>
      <h2 className="font-semibold">3. How We Collect Data</h2>
      <p>We collect personal data when you:</p>
      <ul>
        <li>Create or update an account.</li>
        <li>Place orders or make payments.</li>
        <li>Contact support or submit forms.</li>
        <li>Participate in promotions, surveys, or campaigns.</li>
        <li>
          Use the Platform (through cookies, SDKs, analytics, and server logs).
        </li>
      </ul>
      <h2 className="font-semibold">4. Why We Process Your Data</h2>
      <p>We process personal data for lawful purposes, including:</p>
      <ul>
        <li>Providing and improving Platform services.</li>
        <li>
          Processing orders, payments, deliveries, cancellations, and refunds.
        </li>
        <li>Customer support and grievance handling.</li>
        <li>Fraud prevention, abuse detection, and account security.</li>
        <li>
          Complying with legal obligations (tax, accounting, law enforcement
          requests).
        </li>
        <li>
          Sending service-related communications (OTP, order updates, security
          alerts).
        </li>
        <li>
          Sending marketing communications where permitted by law and your
          preferences.
        </li>
        <li>Analytics, auditing, and internal reporting.</li>
      </ul>
      <p>
        Where consent is required under law, we will request your consent before
        processing and provide mechanisms to withdraw it.
      </p>
      <h2 className="font-semibold">5. Cookies and Tracking Technologies</h2>
      <p>We use cookies and similar technologies to:</p>
      <ul>
        <li>Keep you signed in.</li>
        <li>Remember preferences.</li>
        <li>Measure traffic and performance.</li>
        <li>Personalize content and offers.</li>
      </ul>
      <p>
        You can control cookies through browser/device settings. Disabling
        certain cookies may affect Platform functionality.
      </p>
      <h2 className="font-semibold">6. Sharing of Personal Data</h2>
      <p>We may share personal data with:</p>
      <ul>
        <li>Sellers/merchants to fulfill your orders.</li>
        <li>Payment gateways, banks, and payment processors.</li>
        <li>Logistics and delivery partners.</li>
        <li>
          Cloud hosting, analytics, communications, and customer support
          vendors.
        </li>
        <li>
          Professional advisors (auditors, lawyers) under confidentiality
          obligations.
        </li>
        <li>
          Government authorities, regulators, or law enforcement where required
          by law.
        </li>
        <li>
          Group entities/affiliates for service operations and compliance.
        </li>
      </ul>
      <p>
        We require recipients to process personal data only for approved
        purposes and with reasonable safeguards.
      </p>
      <p>
        For clarity, ITS details collected for community verification are not
        shared with sellers, merchants, or other third parties, except where
        disclosure is required by applicable law.
      </p>
      <h2 className="font-semibold">7. Cross-Border Data Transfers</h2>
      <p>
        Your personal data may be stored or processed outside India where our
        vendors or infrastructure are located, subject to applicable Indian law
        and reasonable contractual/security safeguards.
      </p>
      <h2 className="font-semibold">8. Data Retention</h2>
      <p>We retain personal data only for as long as necessary for:</p>
      <ul>
        <li>Service delivery and business operations.</li>
        <li>Legal, tax, accounting, and regulatory compliance.</li>
        <li>Fraud prevention and dispute resolution.</li>
      </ul>
      <p>
        When no longer required, data is deleted or anonymized in a secure
        manner.
      </p>
      <h2 className="font-semibold">9. Data Security</h2>
      <p>
        We implement reasonable security practices and procedures, including
        administrative, technical, and physical safeguards, to protect personal
        data against unauthorized access, disclosure, alteration, or
        destruction.
      </p>
      <p>
        No method of transmission or storage is completely secure. You are
        responsible for keeping your account credentials confidential.
      </p>
      <h2 className="font-semibold">10. Your Rights Under Indian Law</h2>
      <p>Subject to applicable law, you may have rights to:</p>
      <ul>
        <li>Request a summary of your personal data being processed.</li>
        <li>
          Request correction, completion, updating, or erasure of personal data.
        </li>
        <li>Withdraw consent for processing based on consent.</li>
        <li>Seek grievance redressal through our Grievance Officer.</li>
        <li>
          Nominate another person to exercise rights in case of death or
          incapacity (as permitted under law).
        </li>
      </ul>
      <p>
        To exercise your rights, contact us at privacy@muminsmart.com. We may
        verify identity before acting on requests.
      </p>
      <h2 className="font-semibold">11. Children&#39;s Privacy</h2>
      <p>
        The Platform is not intended for children under 18 years unless use is
        supervised and permitted under applicable law and terms. Where legally
        required, verifiable parental/guardian consent will be obtained.
      </p>
      <h2 className="font-semibold">12. Third-Party Links and Services</h2>
      <p>
        The Platform may contain links to third-party websites/services. We are
        not responsible for their privacy practices. Please review third-party
        policies before sharing data.
      </p>
      <h2 className="font-semibold">13. Grievance Officer</h2>
      <p>In compliance with applicable Indian laws, grievance details are:</p>
      <ul>
        <li>Name: Huzefa</li>
        <li>Email: grievance@muminsmart.com</li>
        <li>
          Address: A78 Hasanji Nagar, Near Emerald Heights School, Indore -
          453331
        </li>
      </ul>
      <p>
        We aim to acknowledge and resolve grievances within timelines prescribed
        by applicable law.
      </p>
      <h2 className="font-semibold">14. Changes to This Policy</h2>
      <p>
        We may update this Privacy Policy from time to time. Material changes
        will be notified through the Platform or by other reasonable means.
        Continued use after updates constitutes acceptance of the revised
        policy.
      </p>
      <h2 className="font-semibold">15. Governing Law and Jurisdiction</h2>
      <p>
        This Privacy Policy is governed by Indian law. Courts at Indore, Madhya
        Pradesh, India shall have jurisdiction, subject to any mandatory legal
        requirements.
      </p>
    </div>
  );
};

const UserPrivacyPolicyDialog = () => {
  return (
    <Dialog>
      <DialogTrigger>
        <button type="button" className="font-semibold text-primary underline">
          Privacy Policy
        </button>
      </DialogTrigger>

      <DialogContent className="max-w-2xl max-h-[85dvh] rounded-3xl">
        <DialogHeader>
          <DialogTitle>Privacy Policy</DialogTitle>
        </DialogHeader>
        <div className="no-scrollbar max-h-[50vh] overflow-y-auto">
          <UserPrivacyPolicy />
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default UserPrivacyPolicyDialog;
